import React, { ReactElement, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '../../../containers/confirmation-modal';
import { EntitiesState } from '../state';
import { CommonProps, PotentialNewOwners, ReassignDocument } from '../types';
import { hasOverlap } from '../../../utils/sets';
import createCommaSeparatedListMessage from '../../../utils/createCommaSeparatedList';

type SubmitHandlerProps = Pick<CommonProps, 'users' | 'groupMap' | 'onSubmit'> & {
  submitInProgress: boolean;
  setSubmitInProgress: (inProgress: boolean) => void;
  entityState: EntitiesState;
};

const UnorderedList = styled.ul`
  padding-left: 2rem;
`;

/**
 * This component handles the actual submission of the reassign qualio entities stepper
 * when the stepper attempts to submit, if it detects an assignee will be added to new groups
 * as a result of reassigning entities, it will prompt the current user to confirm
 */
const SubmitHandler = ({
  users,
  groupMap,
  submitInProgress,
  setSubmitInProgress,
  entityState,
  onSubmit,
}: SubmitHandlerProps): ReactElement | null => {
  const { t } = useTranslation();
  const {
    owner: ownerItems,
    changeRequestOwner: changeRequestOwnerItems,
    approver: approverItems,
    reviewer: reviewerItems,
    issues: issueItems,
    tasks: taskItems,
    default_owner: defaultOwnerItems,
    eventTemplateOwner: eventTemplateOwnerItems,
    controls: controlItems,
    documentTemplateOwner: documentTemplateOwnerItems,
  } = entityState;
  const ownerItemArray = useMemo(() => Object.values(ownerItems), [ownerItems]);
  const changeRequestOwnerItemsArray = useMemo(() => Object.values(changeRequestOwnerItems), [changeRequestOwnerItems]);
  const approverItemArray = useMemo(() => Object.values(approverItems), [approverItems]);
  const reviewerItemArray = useMemo(() => Object.values(reviewerItems), [reviewerItems]);
  const issueItemArray = useMemo(() => Object.values(issueItems), [issueItems]);
  const taskItemArray = useMemo(() => Object.values(taskItems), [taskItems]);
  const defaultOwnerItemArray = useMemo(() => Object.values(defaultOwnerItems), [defaultOwnerItems]);
  const eventTemplateOwnerItemArray = useMemo(() => Object.values(eventTemplateOwnerItems), [eventTemplateOwnerItems]);
  const documentTemplateOwnerItemArray = useMemo(
    () => Object.values(documentTemplateOwnerItems),
    [documentTemplateOwnerItems],
  );
  const controlItemArray = useMemo(() => Object.values(controlItems), [controlItems]);
  const pullGroupName = (groupId: number) => groupMap[groupId].name;

  /**
   * This Reducer is used to take all of the Documents being reassigned to users
   * determine which users will be assigned to new groups as a result. Resulting object looks like:
   * {
   *  userId: new Set(['foo group', 'bar group', 'baz group'])
   * }
   */
  const reduceDocuments = (
    aggregate: Record<string, Set<string>>,
    { newOwner, accessible_by_group_ids: accessibleByGroupIds }: ReassignDocument,
  ) => {
    const newOwnerId = newOwner ? `${newOwner.id}` : null;
    const findUserById = (userObject: PotentialNewOwners) => userObject.value === newOwnerId;
    const user = users.find(findUserById);
    const userGroups = user ? user.groups : [];

    const userHasAccessAlready =
      accessibleByGroupIds.length === 0 ||
      hasOverlap(
        userGroups.map(({ id }) => id),
        accessibleByGroupIds,
      );

    if (userHasAccessAlready || !newOwnerId) {
      return aggregate;
    }

    const existingGroupsUserNeeds = aggregate[newOwnerId] || new Set();

    return {
      ...aggregate,
      [newOwnerId]: new Set([...existingGroupsUserNeeds, ...accessibleByGroupIds.map(pullGroupName)]),
    };
  };

  const groupAssignments = useMemo(() => {
    const allDocuments = [...ownerItemArray, ...approverItemArray, ...reviewerItemArray];

    return allDocuments.reduce(reduceDocuments, {});
  }, [ownerItemArray, approverItemArray, reviewerItemArray]);

  const submitChanges = async () => {
    try {
      await onSubmit({
        owner: ownerItemArray,
        changeRequestOwner: changeRequestOwnerItemsArray,
        approver: approverItemArray,
        reviewer: reviewerItemArray,
        issues: issueItemArray,
        tasks: taskItemArray,
        default_owner: defaultOwnerItemArray,
        eventTemplateOwner: eventTemplateOwnerItemArray,
        control: controlItemArray,
        documentTemplateOwner: documentTemplateOwnerItemArray,
      });
    } finally {
      setSubmitInProgress(false);
    }
  };

  // If "submitInProgress" is false, returns null so nothing is put into the DOM
  if (!submitInProgress) {
    return null;
  }

  const usersGettingNewGroups = Object.keys(groupAssignments)
    .map((userId) => users.find((userObject) => userObject.value === userId))
    .filter(Boolean) as PotentialNewOwners[];

  /**
   * If there are no values in usersGettingNewGroups, then we do not need to show
   * the warning modal, so the component can just submit the changes and return
   * null.
   */
  if (!usersGettingNewGroups.length) {
    submitChanges();

    return null;
  }

  /**
   * When a "Submit" happens that would result in new groups being assigned to a
   * user, we show the confirmation modal before submitting the changes
   */
  return (
    <ConfirmationModal
      title={t('reassignEntities.addUserToGroup')}
      closeModal={() => setSubmitInProgress(false)}
      buttonProps={{
        label: t('reassignEntities.confirm'),
        onClick: () => {
          submitChanges();
        },
      }}
    >
      {t('reassignEntities.warningMessage')}
      <UnorderedList>
        {usersGettingNewGroups.map((user) => {
          const { value: userId, label: userName } = user;
          const groupNames = Array.from(groupAssignments[userId]);

          const messagePrefix = (
            <span>
              <strong>{userName}</strong> will be added to the groups:{' '}
            </span>
          );

          return (
            <li key={userId}>
              <span>{createCommaSeparatedListMessage(groupNames, { bold: true, messagePrefix })}</span>
            </li>
          );
        })}
      </UnorderedList>
    </ConfirmationModal>
  );
};

export default SubmitHandler;
